import React, { useState } from 'react';
import { BellRing, CheckCircle2, Fingerprint, Loader2, MapPin, TriangleAlert } from 'lucide-react';
import { toast } from 'sonner';
import { startRegistration, browserSupportsWebAuthn } from '@simplewebauthn/browser';
import { errMsg } from '../portal/api';
import { enablePush } from '../lib/push';
import { btn, gold, tapi } from './tapi';

const d = (s) => new Date(`${s}T12:00:00`).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' });
const card = 'rounded-2xl border border-[#222] bg-[#0D0D0D] p-4';

export function Setup({ me, reload }) {
  const [busy, setBusy] = useState('');
  const [pushOn, setPushOn] = useState(typeof Notification !== 'undefined' && Notification.permission === 'granted');
  const bio = async () => {
    setBusy('bio');
    try { const { data } = await tapi.post('/passkey/register/options'); const att = await startRegistration({ optionsJSON: data }); await tapi.post('/passkey/register/verify', att); localStorage.setItem('bond_traveler_bio', '1'); toast.success('Accesso con Face ID / impronta attivato'); reload(); }
    catch (e) { toast.error(e.name === 'NotAllowedError' ? 'Registrazione annullata' : errMsg(e)); } finally { setBusy(''); }
  };
  const push = async () => {
    setBusy('push');
    try { await enablePush((sub) => tapi.post('/push/subscribe', sub)); setPushOn(true); toast.success('Avvisi attivi su questo telefono'); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(''); }
  };
  if (me.has_passkey && pushOn) return null;
  return (
    <div className={`${card} space-y-3`} data-testid="traveler-setup">
      <p className="text-sm font-semibold">Completa l'attivazione</p>
      {!pushOn && <button onClick={push} disabled={!!busy} className={btn} style={gold} data-testid="traveler-enable-push">{busy === 'push' ? <Loader2 className="animate-spin" size={18} /> : <BellRing size={18} />}Attiva gli avvisi</button>}
      {!me.has_passkey && browserSupportsWebAuthn() && <button onClick={bio} disabled={!!busy} className={`${btn} border border-[#D4AF37] text-[#D4AF37]`} data-testid="traveler-enable-bio">{busy === 'bio' ? <Loader2 className="animate-spin" size={18} /> : <Fingerprint size={18} />}Accedi con Face ID / impronta</button>}
      <p className="text-xs text-[#888]">Con Face ID o impronta non servono più codici via email, anche all'estero.</p>
    </div>
  );
}

export function Arrivals({ items, reload }) {
  const confirm = (a) => tapi.post(`/arrivals/${a.id}/confirm`).then(() => { toast.success('Arrivo confermato'); reload(); }).catch((e) => toast.error(errMsg(e)));
  if (!items.length) return null;
  return items.map((a) => (
    <div key={a.id} className={`${card} border-[#D4AF37]/60`} data-testid={`traveler-arrival-${a.id}`}>
      <p className="font-semibold mb-1">Sei arrivato a {[a.city, a.country].filter(Boolean).join(', ')}?</p>
      <p className="text-xs text-[#A3A3A3] mb-3">Arrivo previsto {a.expected} · conferma entro {a.due} (ora locale)</p>
      {a.can_confirm ? <button onClick={() => confirm(a)} className={btn} style={gold} data-testid={`traveler-arrival-confirm-${a.id}`}><CheckCircle2 size={18} />Confermo di essere arrivato</button>
        : <p className="text-xs text-[#888]">Potrai confermare dalle {a.opens}.</p>}
    </div>
  ));
}

export function Trips({ trips }) {
  if (!trips.length) return <p className="text-sm text-[#A3A3A3]">Nessuna trasferta in corso o programmata.</p>;
  return trips.map((t) => (
    <div key={t.id} className={card} data-testid={`traveler-trip-${t.id}`}>
      <p className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] mb-1">{t.active ? 'In corso' : 'Programmata'}{t.company ? ` · ${t.company}` : ''}</p>
      {t.stages.map((s) => (
        <div key={s.id} className="py-2 border-t border-[#1A1A1A] first:border-0">
          <p className="font-semibold inline-flex items-center gap-1.5"><MapPin size={15} className="text-[#D4AF37]" />{[s.city, s.country].filter(Boolean).join(', ')}</p>
          <p className="text-xs text-[#888]">{d(s.start)} – {d(s.end)}</p>
          {s.news.map((n, i) => <details key={i} className="mt-2 text-sm"><summary className="text-[#E5E5E5] cursor-pointer">{n.title}</summary><div className="text-xs text-[#A3A3A3] mt-1 [&_a]:text-[#D4AF37]" dangerouslySetInnerHTML={{ __html: n.text }} /></details>)}
        </div>
      ))}
    </div>
  ));
}

export function Alerts({ items }) {
  if (!items.length) return null;
  return (
    <div className={card} data-testid="traveler-alerts">
      <p className="text-[11px] uppercase tracking-[0.2em] text-[#E37A72] mb-2 inline-flex items-center gap-1.5"><TriangleAlert size={13} />Avvisi ricevuti</p>
      {items.map((a, i) => (
        <div key={i} className="py-2 border-t border-[#1A1A1A] first:border-0 text-sm">
          <p className="font-semibold">{a.kind === 'event' ? 'Evento grave' : 'Rischio in aumento'} · {a.country}</p>
          <p className="text-xs text-[#888]">{new Date(a.sent_at).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' })} · {(a.changes || []).map((c) => c.name).slice(0, 2).join(', ')}</p>
        </div>
      ))}
    </div>
  );
}
